import FormatQuote from '@mui/icons-material/FormatQuote';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton } from '@/components/atoms';

import { type ViewerCaption as Caption } from './types';
import { VIEWER_INK, VIEWER_INK_MUTED } from './viewerTokens';

type Props = {
  caption: Caption;
  /**
   * `panel` sits below a photograph in the lightbox, where there is room to
   * read. `overlay` sits on top of a playing clip in the reel, where there is
   * not — so the quotation is clamped and the type is a step smaller.
   */
  variant: 'panel' | 'overlay';
};

/** Three lines of a quotation, after which it is cut off with an ellipsis. */
const quoteClamp = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 3,
  overflow: 'hidden',
};

/**
 * The words that go with a medium, in both viewers.
 *
 * One component rather than two because the content is identical — the car,
 * where it went and when, what the customer said, one way to act on it — and
 * only the room it is read in differs. Writing it twice is how the reel ends
 * up quoting a customer the lightbox does not.
 *
 * The quotation is clamped in the reel and not in the lightbox. Over a playing
 * video, a paragraph covers the thing the visitor came to watch, and a clip
 * that loops every eight seconds is not where anybody reads to the end of a
 * testimonial; under a still photograph there is nothing to cover and no
 * clock. The full text is in the lightbox and on the gallery's own cards, so
 * nothing is only ever available clamped.
 *
 * Attribution is dropped when there is none, rather than printed as
 * "Anonymous". A customer who declined to be named did not ask to be labelled.
 */
export function ViewerCaption({ caption, variant }: Props) {
  const overlay = variant === 'overlay';

  return (
    <Stack spacing={overlay ? 2 : 3} sx={{ maxWidth: overlay ? 520 : 760 }}>
      <Box>
        <Typography
          variant={overlay ? 'subtitle1' : 'h6'}
          component="p"
          sx={{ color: VIEWER_INK, fontWeight: 700, lineHeight: 1.3 }}
        >
          {caption.title}
        </Typography>
        <Typography variant="body2" sx={{ color: VIEWER_INK_MUTED, mt: 1 }}>
          {caption.meta}
        </Typography>
      </Box>

      {caption.quote && (
        <Stack direction="row" spacing={2} component="blockquote" sx={{ m: 0 }}>
          <FormatQuote
            aria-hidden
            sx={{ color: 'primary.main', flexShrink: 0, fontSize: 24, mt: '-2px' }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: VIEWER_INK,
                fontStyle: 'italic',
                lineHeight: 1.6,
                ...(overlay ? quoteClamp : {}),
              }}
            >
              {caption.quote.text}
            </Typography>
            {caption.quote.attribution && (
              <Typography
                component="cite"
                variant="caption"
                sx={{ color: VIEWER_INK_MUTED, display: 'block', mt: 1, fontStyle: 'normal' }}
              >
                {caption.quote.attribution}
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      {caption.action && (
        <AppButton to={caption.action.to} sx={{ alignSelf: 'flex-start' }}>
          {caption.action.label}
        </AppButton>
      )}
    </Stack>
  );
}
